import React, { useEffect, useRef } from "react";
import "./NeuralNetwork.css";

function NeuralNetwork() {
  const canvasRef = useRef(null);
  const animationRef = useRef(null);
  const nodesRef = useRef([]);
  const connectionsRef = useRef([]);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");

    const resizeCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };

    resizeCanvas();
    window.addEventListener("resize", resizeCanvas);

    // Create nodes
    const createNodes = () => {
      const nodes = [];
      const layers = 4;
      const nodesPerLayer = [3, 5, 5, 3];

      for (let layer = 0; layer < layers; layer++) {
        const layerNodes = [];
        const x = (layer + 1) * (canvas.width / (layers + 1));

        for (let i = 0; i < nodesPerLayer[layer]; i++) {
          const y = (i + 1) * (canvas.height / (nodesPerLayer[layer] + 1));
          layerNodes.push({
            x,
            y,
            vx: (Math.random() - 0.5) * 0.5,
            vy: (Math.random() - 0.5) * 0.5,
            radius: 4 + Math.random() * 4,
            layer,
            activation: Math.random(),
          });
        }
        nodes.push(layerNodes);
      }
      return nodes;
    };

    // Create connections
    const createConnections = (nodes) => {
      const connections = [];

      for (let layer = 0; layer < nodes.length - 1; layer++) {
        for (let i = 0; i < nodes[layer].length; i++) {
          for (let j = 0; j < nodes[layer + 1].length; j++) {
            connections.push({
              from: nodes[layer][i],
              to: nodes[layer + 1][j],
              strength: Math.random(),
              pulse: 0,
            });
          }
        }
      }
      return connections;
    };

    nodesRef.current = createNodes();
    connectionsRef.current = createConnections(nodesRef.current);

    // Animation loop
    const animate = () => {
      ctx.fillStyle = "rgba(255, 255, 255, 0.05)";
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // Update and draw connections
      connectionsRef.current.forEach((connection) => {
        // Randomly pulse connections
        if (Math.random() < 0.005) {
          connection.pulse = 1;
        }

        if (connection.pulse > 0) {
          connection.pulse -= 0.02;
        }

        const opacity =
          0.2 + connection.strength * 0.3 + connection.pulse * 0.6;
        ctx.strokeStyle =
          connection.pulse > 0
            ? `rgba(227, 30, 36, ${opacity})`
            : `rgba(74, 74, 74, ${opacity})`;

        ctx.lineWidth = 1.5 + connection.pulse * 2;
        ctx.beginPath();
        ctx.moveTo(connection.from.x, connection.from.y);
        ctx.lineTo(connection.to.x, connection.to.y);
        ctx.stroke();
      });

      // Update and draw nodes
      nodesRef.current.forEach((layer) => {
        layer.forEach((node) => {
          // Gentle floating motion
          node.x += node.vx;
          node.y += node.vy;

          // Boundary check with soft bounce
          if (node.x < 50 || node.x > canvas.width - 50) node.vx *= -0.9;
          if (node.y < 50 || node.y > canvas.height - 50) node.vy *= -0.9;

          // Random activation
          if (Math.random() < 0.02) {
            node.activation = 1;
          }
          if (node.activation > 0) {
            node.activation -= 0.01;
          }

          // Draw node shadow
          ctx.fillStyle = "rgba(0, 0, 0, 0.1)";
          ctx.beginPath();
          ctx.arc(
            node.x + 2,
            node.y + 2,
            node.radius + node.activation * 10,
            0,
            Math.PI * 2
          );
          ctx.fill();

          // Draw node
          const gradient = ctx.createRadialGradient(
            node.x,
            node.y,
            0,
            node.x,
            node.y,
            node.radius * 2
          );

          if (node.activation > 0) {
            gradient.addColorStop(
              0,
              `rgba(227, 30, 36, ${0.8 + node.activation * 0.2})`
            );
            gradient.addColorStop(
              0.5,
              `rgba(227, 30, 36, ${0.4 + node.activation * 0.4})`
            );
            gradient.addColorStop(1, "rgba(227, 30, 36, 0)");
          } else {
            gradient.addColorStop(0, "rgba(74, 74, 74, 0.9)");
            gradient.addColorStop(0.5, "rgba(74, 74, 74, 0.4)");
            gradient.addColorStop(1, "rgba(74, 74, 74, 0)");
          }

          ctx.fillStyle = gradient;
          ctx.beginPath();
          ctx.arc(
            node.x,
            node.y,
            node.radius + node.activation * 10,
            0,
            Math.PI * 2
          );
          ctx.fill();

          // Draw node core
          ctx.fillStyle = node.activation > 0 ? "#E31E24" : "#4A4A4A";
          ctx.beginPath();
          ctx.arc(node.x, node.y, node.radius * 0.5, 0, Math.PI * 2);
          ctx.fill();
        });
      });

      animationRef.current = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      window.removeEventListener("resize", resizeCanvas);
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, []);

  return <canvas ref={canvasRef} className="neural-network" />;
}

export default NeuralNetwork;
